import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Building2, ChevronRight, Plus } from 'lucide-react';
import { meQuery } from '../../api/queries';
import { AppShell } from '../../app/shell/AppShell';
import { useI18n } from '../../i18n/I18nProvider';
import { UserText } from '../../ui/Bidi';
import { ButtonLink } from '../../ui/Button';
import { Badge, EmptyState } from '../../ui/Feedback';
import { Page, PageHeader, Panel, useDocumentTitle } from '../../ui/Layout';
import { QueryError } from '../../ui/QueryError';
import styles from './Orgs.module.css';

export function ChooseOrgPage() {
  const { t } = useI18n();
  useDocumentTitle(t.orgs.chooseTitle);
  const me = useQuery(meQuery);
  const organizations = me.data?.organizations ?? [];

  return (
    <AppShell>
      <Page narrow>
        <PageHeader
          title={t.orgs.chooseTitle}
          subtitle={t.orgs.chooseSubtitle}
          actions={
            <ButtonLink
              to="/orgs/new"
              variant="primary"
              icon={<Plus size={16} aria-hidden="true" />}
            >
              {t.shell.createOrganization}
            </ButtonLink>
          }
        />
        {me.error ? <QueryError error={me.error} onRetry={() => void me.refetch()} /> : null}
        <Panel flush>
          {organizations.length === 0 ? (
            <EmptyState
              icon={<Building2 size={22} />}
              title={t.orgs.emptyTitle}
              action={
                <ButtonLink to="/orgs/new" variant="primary">
                  {t.shell.createOrganization}
                </ButtonLink>
              }
            >
              {t.orgs.emptyBody}
            </EmptyState>
          ) : (
            <ul className={styles.orgList}>
              {organizations.map((org) => (
                <li key={org.id}>
                  <Link to="/orgs/$orgId" params={{ orgId: org.id }} className={styles.orgRow}>
                    <span className={styles.orgIcon} aria-hidden="true">
                      <Building2 size={18} />
                    </span>
                    <span className={styles.orgText}>
                      <span className={styles.orgName}>
                        <UserText>{org.name}</UserText>
                      </span>
                      <span className={styles.orgMeta}>
                        {t.roles[org.role as keyof typeof t.roles] ?? org.role}
                      </span>
                    </span>
                    <Badge tone={org.etaEnvironment === 'production' ? 'accent' : 'neutral'}>
                      {t.env[org.etaEnvironment]}
                    </Badge>
                    <ChevronRight size={18} className="flip-rtl" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </Page>
    </AppShell>
  );
}
